import { useCallback, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';

import UploadCardSide from './UploadCardSide';
import EditCardModal from '../EditCardModal/EditCardModal';

import { Alert, Button, Typography } from '@vivaahealth/design-system';
import { styled } from 'styled-components';
import { CaptureType, PictureState, UploadError } from '../../types';
import { Side } from '../../../../../utils/enum';
import { useAsyncFlow } from '../../../../../hooks/common/useAsyncFlow';
import { InfoMessage } from '../../../../../components/InfoMessage';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const UploadCardContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px 8px;
`;

interface UploadCardProps {
  onContinue: (pictures: PictureState) => Promise<void>;
}

const UploadCard = ({ onContinue }: UploadCardProps) => {
  const { t } = useTranslation();
  const [pictures, setPictures] = useState<PictureState>({
    [Side.Front]: null,
    [Side.Back]: null,
  });
  const [showEditCardModal, setShowEditCardModal] = useState(false);
  const [currentError, setCurrentError] = useState<UploadError>(null);
  const continueDisabled = !pictures[Side.Front] || !pictures[Side.Back];
  const [mostRecentlyUpdatedSide, setMostRecentlyUpdatedSide] = useState<Side>(
    Side.Front,
  );

  const onCapture = useCallback(
    (side: Side) => (blob: Blob | null, type: CaptureType) => {
      setPictures((previousPictures: PictureState) => ({
        ...previousPictures,
        [side]: blob,
      }));
      if (type === 'upload') {
        setShowEditCardModal(true);
        setMostRecentlyUpdatedSide(side);
      }
    },
    [setShowEditCardModal, setMostRecentlyUpdatedSide, setPictures],
  );

  const onRemove = useCallback(
    (side: Side) => () => {
      setPictures((previousPictures: PictureState) => ({
        ...previousPictures,
        [side]: null,
      }));
    },
    [setPictures],
  );

  const {
    execute: onContinueAsync,
    pending,
    error: uploadError,
  } = useAsyncFlow(onContinue);

  return (
    <Container>
      <Typography variant="headline-small" bold>
        {t('title', { ns: 'uploadCard' })}
      </Typography>
      {currentError && (
        <Alert variant="danger">
          {t(
            currentError === 'incorrect_filetype'
              ? 'fileTypeAlertMessage'
              : 'pdfConversionFailureMessage',
          )}
        </Alert>
      )}
      <UploadCardContainer>
        <UploadCardSide
          message={t('addFrontInstructions', { ns: 'uploadCard' })}
          side={Side.Front}
          uploadedBlob={pictures[Side.Front]}
          onCapture={onCapture(Side.Front)}
          onRemove={onRemove(Side.Front)}
          setCurrentError={setCurrentError}
        />
        <UploadCardSide
          message={t('addBackInstructions', { ns: 'uploadCard' })}
          side={Side.Back}
          uploadedBlob={pictures[Side.Back]}
          onCapture={onCapture(Side.Back)}
          onRemove={onRemove(Side.Back)}
          setCurrentError={setCurrentError}
        />
      </UploadCardContainer>
      {uploadError && <InfoMessage>{t('errorOccurredTryAgain')}</InfoMessage>}
      <Button
        data-testid="continue-with-uploads"
        disabled={continueDisabled || pending}
        onClick={async () => {
          await onContinueAsync(pictures);
        }}
        fullWidth
      >
        {t('continue', { ns: 'uploadCard' })}
      </Button>
      {showEditCardModal && pictures[mostRecentlyUpdatedSide] !== null && (
        <EditCardModal
          currentSide={mostRecentlyUpdatedSide}
          // We have to cast here because of the dynamic access to `pictures` both in the conditional and in the prop
          picture={pictures[mostRecentlyUpdatedSide] as Blob}
          onSave={(picture) => {
            setPictures({ ...pictures, [mostRecentlyUpdatedSide]: picture });
          }}
          onClose={() => setShowEditCardModal(false)}
          onDismiss={() => {
            setPictures({ ...pictures, [mostRecentlyUpdatedSide]: null });
          }}
        />
      )}
    </Container>
  );
};

export default withTranslation()(UploadCard);
